<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import V4Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import DashboardWidget from './DashboardWidget.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'remove', 'update:title']);

const { t } = useI18n();

const fallbackTitle = computed(
  () =>
    props.widget.report_name ||
    t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGET_LABEL', { index: props.index + 1 })
);

const onTitleInput = e => emit('update:title', e.target.value);

const stop = e => e.stopPropagation();

const onCardClick = () => emit('select');

const onRemove = e => {
  e.stopPropagation();
  emit('remove');
};
</script>

<template>
  <div
    class="group relative bg-n-solid-2 rounded-lg border-2 transition cursor-pointer overflow-hidden flex flex-col"
    :class="
      isSelected
        ? 'border-n-brand shadow-md'
        : 'border-transparent hover:border-n-slate-6'
    "
    @click="onCardClick"
  >
    <div
      class="flex items-center gap-2 px-3 py-2 border-b border-n-weak bg-n-alpha-1"
    >
      <Icon
        icon="i-lucide-grip-vertical"
        class="size-4 text-n-slate-10 cursor-grab drag-handle shrink-0"
        :title="t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGETS_DESCRIPTION')"
      />
      <div class="flex-1 min-w-0">
        <input
          :value="widget.title"
          :placeholder="fallbackTitle"
          type="text"
          class="reset-base w-full text-sm font-medium text-n-slate-12 bg-transparent border-0 outline-none placeholder:text-n-slate-11 truncate"
          @click="stop"
          @input="onTitleInput"
        />
        <p
          v-if="widget.report_name && widget.title"
          class="text-xs text-n-slate-11 truncate"
        >
          {{ widget.report_name }}
        </p>
      </div>
      <span
        class="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 shrink-0"
      >
        <Icon
          :icon="
            widget.width === 'full'
              ? 'i-lucide-rectangle-horizontal'
              : 'i-lucide-square'
          "
          class="size-3"
        />
        {{
          widget.width === 'full'
            ? t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDTHS.FULL')
            : t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDTHS.HALF')
        }}
      </span>
      <V4Button
        type="button"
        icon="i-lucide-trash-2"
        size="xs"
        variant="ghost"
        color="ruby"
        class="opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
        :class="{ '!opacity-100': isSelected }"
        :title="t('WGPT_CUSTOM_DASHBOARDS.FORM.REMOVE_WIDGET')"
        @click="onRemove"
      />
    </div>

    <div class="p-1 min-h-[260px] pointer-events-none">
      <DashboardWidget v-if="widget.wgpt_custom_report_id" :widget="widget" />
      <div
        v-else
        class="flex flex-col items-center justify-center h-full text-center px-4 py-8 text-n-slate-11"
      >
        <Icon icon="i-lucide-image-off" class="size-8 mb-2 text-n-slate-9" />
        <p class="text-sm">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.SELECT_REPORT') }}
        </p>
      </div>
    </div>
  </div>
</template>
